import React, { useState, useEffect } from "react";
import { TSearchData, TTableData } from "../Simulation";
import FundService, { FundWithExtraDataType } from "../../../services/FundService";
import SearchableDropdown, { TOptionType } from "../../../components/SearchableDropdown/SearchableDropdown";
import { RatioKeys, TRatiosKeyCompletos } from "../../../services/OmniaService";
import { RATIOS_OPTIMIZAR } from "../Texts/DropdownData";
import { ArrowShortDown, ArrowShortUp } from "../../../components/icons/Arrows";
import { Dropdown, IDropdownOption } from "@fluentui/react";
import { dropdownStylesCell } from "../Styles/DropDownStyles";
import { DictionaryRatios } from "../Utils/Dictionary";
import { TRatioAndPeriod } from "../Tabs/Backtesting/Backtesting";
import { NumberToPercentaje } from "../Utils/ReplaceNumbersFunctions";

const BASE_CLASS = "tablesSimulation";

type TTableSimulationProps = {
    data: TTableData[];
    ratioAndPeriod: TRatioAndPeriod;
    onSelectedDestinyFunds: (selectedItems: TSelectionState) => void;
};

export type TSelectionState = Record<string, { checked: boolean; data: TTableData }>;

const TableDestino: React.FC<TTableSimulationProps> = ({ data, ratioAndPeriod, onSelectedDestinyFunds }) => {
    const localTheme = window.localStorage.getItem("theme") || "light";
    const [destinyUpdates, setDestinyUpdates] = useState<TSelectionState>({ });
    const [newInput, setNewInput] = useState<string>('');
    const [fundsOptions, setFundsOptions] = useState<TOptionType[]>([]);
    const [ratio, setRatio] = useState<RatioKeys>(ratioAndPeriod.ratio);
    const [ratiosFiltrados, setRatiosFiltrados] = useState<IDropdownOption[]>([]);
    const [initialLoad, setInitialLoad] = useState<boolean>(false);

    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    useEffect(() => {
        setRatio(ratioAndPeriod.ratio);
    }, [ratioAndPeriod])

    useEffect(() => {
        data.forEach(item => {
            setDestinyUpdates((prev => ({
                ...prev,
                [item.originalIsin]: {
                    checked: false,
                    data: {
                        destinyIsin: item.destinyIsin || '',
                        destinyName: item.destinyName || '',
                        originalIsin: item.originalIsin,
                        originalName: item.originalName,
                        originalWeight: item.originalWeight,
                        destinyWeight: item.destinyWeight || 0,
                        posicion_fondo_original: 0,
                        total_fondos_categoria: 0,
                        ratiosCompletos: item?.ratiosCompletos || {
                            fondo_original: {} as TRatiosKeyCompletos,
                            fondo_nuevo: {} as TRatiosKeyCompletos,
                        }
                    } 
                },
            })))
        });
        setInitialLoad(true);
    }, [data]);

    const allSelected = Object.values(destinyUpdates).length > 0 &&
        Object.values(destinyUpdates).every(item => item.checked);

    const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        const updated: TSelectionState = {};
        Object.keys(destinyUpdates).forEach(isin => {
            updated[isin] = { data: { ...destinyUpdates[isin].data }, checked };
        });
        setDestinyUpdates(updated);
    };

    const handleItemCheckChange = (isin: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setDestinyUpdates(prev => ({
            ...prev,
            [isin]: {
                data: { ...prev[isin].data },
                checked: event.target.checked,
            },
        }));
    };

    const onSelectFund = (selected: TSearchData) => {
        setDestinyUpdates(prev => ({
            ...prev,
            [selected.originalIsin]: {
                checked: prev[selected.originalIsin]?.checked,
                data: {
                    ...selected,
                    destinyWeight: prev[selected.originalIsin].data.destinyWeight,
                    originalWeight: prev[selected.originalIsin].data.originalWeight,
                    ratiosCompletos: prev[selected.originalIsin].data?.ratiosCompletos,
                    total_fondos_categoria: prev[selected.originalIsin].data.total_fondos_categoria,
                    posicion_fondo_original: prev[selected.originalIsin].data.posicion_fondo_original,
                }
            }
        }));
    };

    const handleWeightChange = (isin: string, defaultWeight: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const newWeight = Number(event.target.value);
        setDestinyUpdates(prev => ({
            ...prev,
            [isin]: {
                checked: prev[isin].checked,
                data: {
                    ...prev[isin].data,
                    destinyWeight: isNaN(newWeight) ? defaultWeight : newWeight,
                }
            },
        }));
    };

    const fetchFunds = async () => {
        const response = await FundService.getFunds(0, 100);

        const result: TOptionType[] = response.results.map((broher: FundWithExtraDataType ) => {
            return {
                label: broher.name,
                value: broher.isin
            }
        });
        setFundsOptions(result);
    };

    useEffect(() => {
        fetchFunds();
    },[]);

    useEffect(() => {
        onUpdateInput();
    }, [newInput]);

    useEffect(() => {
        if (initialLoad) onSelectedDestinyFunds(destinyUpdates);
    }, [destinyUpdates])

    const onUpdateInput = async () => {

        if (newInput.length > 4)
        {
            await delay(1500);
            FundService.getSearchFunds(newInput.toUpperCase(), 25).then((filteredFunds) =>
            {
                const filteredFundsOptions = filteredFunds.map((fund: any) =>
                {
                    return{
                        label: fund.name,
                        value: fund.isin
                    }
                });
                setFundsOptions(filteredFundsOptions);
            })
        }
    };

    useEffect(() => {
        ratiosFilter(ratio);
    }, [data]);

    const ratiosFilter = (ratioFilter: RatioKeys) => {
        switch (ratioFilter) {
            case "gastos_corrientes":
                setRatiosFiltrados(
                    RATIOS_OPTIMIZAR.filter(ratiosOp =>
                        ratiosOp.key !== "pca" && ratiosOp.key !== "clase_limpia"
                    )
                );
                break;
            case "pca":
                setRatiosFiltrados(
                    RATIOS_OPTIMIZAR.filter(ratiosOp =>
                        ratiosOp.key !== "gastos_corrientes" && ratiosOp.key !== "clase_limpia"
                    )
                );
                break;
            case "clase_limpia":
                setRatiosFiltrados(
                    RATIOS_OPTIMIZAR.filter(ratiosOp =>
                        ratiosOp.key !== "pca" && ratiosOp.key !== "gastos_corrientes"
                    )
                );
                break;
            default:
                setRatiosFiltrados(
                    RATIOS_OPTIMIZAR.filter(ratiosOp =>
                        ratiosOp.key !== "pca" &&
                        ratiosOp.key !== "gastos_corrientes" &&
                        ratiosOp.key !== "clase_limpia"
                    )
                );
                break;
        }
    };

    return (
        <div className={BASE_CLASS}>

            <table>
                <thead>
                    <tr className={`${BASE_CLASS}__${localTheme}`}>
                        <th>Ranking Original</th> 
                        <th>
                            <div className={`${BASE_CLASS}__ratios`}>
                                <Dropdown
                                    placeholder={DictionaryRatios(ratio) ?? "Ratio"}
                                    className={`${BASE_CLASS}__dropdown`}
                                    options={ratiosFiltrados}
                                    defaultSelectedKeys={[RATIOS_OPTIMIZAR[0].key as string]}
                                    onChange={(event, option) => setRatio(option?.key as RatioKeys)}
                                    styles={dropdownStylesCell(localTheme)}
                                    calloutProps={{
                                        styles: {
                                            root: { width: 200 } // ancho del modal desplegable
                                    }}}
                                />
                                <span>{ratioAndPeriod?.period?.toUpperCase()} / Original</span>
                            </div>
                        </th>
                        <th>
                            {DictionaryRatios(ratio) ?? DictionaryRatios(ratio)} / {ratioAndPeriod?.period?.toUpperCase()} / Nuevo
                        </th>
                        <th>Fondo Nuevo</th>
                        <th>Peso %</th>
                        <th>
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={handleSelectAllChange}
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((item, index) => (
                        <tr key={index}>
                            <td>
                                <div className={`${BASE_CLASS}__center__text`}>
                                    {item?.posicion_fondo_original || 0} / {item.total_fondos_categoria}
                                </div>
                            </td>
                            <td>
                                <div className={`${BASE_CLASS}__center__text`}>
                                    {NumberToPercentaje(item.ratiosCompletos.fondo_original[ratio], ratio)}
                                </div>
                            </td>
                            <td>
                                <div className={`${BASE_CLASS}__arrows`}>
                                    {NumberToPercentaje(item.ratiosCompletos.fondo_nuevo[ratio], ratio)}
                                    {item?.ratiosCompletos.fondo_original[ratio] > item?.ratiosCompletos?.fondo_nuevo[ratio]
                                        ? <ArrowShortDown color={'red'} size={'14'} />
                                        : <ArrowShortUp color={'green'} size={'14'} />  
                                    }
                                </div>
                            </td>
                            <td>
                                <SearchableDropdown options={fundsOptions} onSelect={(selected) => onSelectFund(selected)} defaultOption={item} onChangeInput={(event) => setNewInput(event)} onFocusInput={() => {
                                    fetchFunds();
                                }}/>
                                {item.destinyIsin === '' ? item.originalIsin : item.destinyIsin}
                            </td>
                            <td>
                                <input className={`${BASE_CLASS}__weight`}
                                    type="number"
                                    defaultValue={(item.destinyWeight ?? 0).toString()}
                                    onChange={handleWeightChange(item.originalIsin, item.destinyWeight)}
                                />                                
                            </td>
                            <td>
                                <input className={`${BASE_CLASS}__checkbox`}
                                    type="checkbox"
                                    checked={destinyUpdates[item.originalIsin]?.checked ?? false}
                                    onChange={handleItemCheckChange(item.originalIsin)}
                                />
                            </td>
                        </tr>
                    ))}
                    <tr>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className={`${BASE_CLASS}__total`}> Total: </td>
                        <td>
                            <div className={`${BASE_CLASS}__total`}>
                               {Object.values(destinyUpdates).reduce((acc, fund) => acc += fund.data.destinyWeight, 0) > 100 ? 100 : Object.values(destinyUpdates).reduce((acc, fund) => acc += fund.data.destinyWeight, 0).toFixed(2)}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default TableDestino;
